import React, {useEffect, useRef, useState} from 'react';
import {Alert, Badge, Button, Card, Col, Form, Row, Table} from 'react-bootstrap';
import syncAuditApi from '../../services/syncAuditApi';

const value = input => input == null || input === '' ? '—' : String(input);
const time = input => {
  if (!input || String(input).startsWith('0000-00-00')) return '—';
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? value(input) : date.toLocaleString();
};
const payload = response => {
  if (response?.status === 'FAIL' || response?.data?.status === 'FAIL') {
    throw new Error(response.message || response.data.message || 'Unable to load sync records');
  }
  return response?.data?.data ?? response?.data ?? response;
};
const errorText = error => error.response?.data?.message || error.message || 'Unable to load sync records';
const PAGE_SIZE = 20;

export default function ItemSyncCheck() {
  const [branches, setBranches] = useState([]), [branchesLoading, setBranchesLoading] = useState(true);
  const [branchError, setBranchError] = useState(''), [branchId, setBranchId] = useState('');
  const [refCode, setRefCode] = useState(''), [applied, setApplied] = useState(null);
  const [data, setData] = useState(null), [error, setError] = useState(''), [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(''), [to, setTo] = useState('');
  const [retry, setRetry] = useState(0);
  const sequence = useRef(0);

  useEffect(() => {
    let active = true;
    setBranchesLoading(true); setBranchError('');
    syncAuditApi.listItemSyncBranches().then(response => {
      const rows = payload(response);
      if (!Array.isArray(rows)) throw new Error('Unable to load branch list');
      if (active) setBranches(rows);
    }).catch(err => {if (active) setBranchError(errorText(err));})
      .finally(() => {if (active) setBranchesLoading(false);});
    return () => {active = false;};
  }, [retry]);

  useEffect(() => {
    const current = ++sequence.current;
    if (!applied) {setData(null); setLoading(false); return;}
    setLoading(true); setData(null); setError('');
    syncAuditApi.getItemSyncRecords({...applied, limit: PAGE_SIZE}).then(response => {
      const next = payload(response);
      if (!next?.branch || !Array.isArray(next.items) || !Array.isArray(next.changes?.rows)
        || !Array.isArray(next.unmatched)) throw new Error('Invalid item sync report response');
      if (sequence.current === current) setData(next);
    }).catch(err => {if (sequence.current === current) setError(errorText(err));})
      .finally(() => {if (sequence.current === current) setLoading(false);});
    return () => {sequence.current++;};
  }, [applied]);

  const clearResult = () => {sequence.current++; setApplied(null); setData(null); setError(''); setLoading(false);};
  const search = event => {
    event.preventDefault();
    if (!branchId || !refCode.trim()) {setError('Select a branch and enter an item reference code.'); return;}
    if (from && to && from > to) {setError('From date must be on or before to date.'); return;}
    setApplied({branchId, refCode: refCode.trim(), from, to, page: 0});
  };
  const changePage = page => setApplied(previous => ({...previous, page}));

  return <Card className="mb-4">
    <Card.Body>
      <h5>Item Sync Check</h5>
      <p className="text-muted">Search an exact item reference code in a branch to check current database stock, prices, and saved sync history.</p>
      {branchError && <Alert variant="danger">{branchError} <Button size="sm" variant="outline-danger" onClick={() => setRetry(n => n + 1)}>Retry branches</Button></Alert>}
      <Form onSubmit={search}>
        <Row className="g-2 align-items-end">
          <Col md={5}>
            <Form.Group controlId="item-sync-branch">
              <Form.Label>Branch</Form.Label>
              <Form.Select value={branchId} disabled={branchesLoading} required onChange={e => {setBranchId(e.target.value); clearResult();}}>
                <option value="">{branchesLoading ? 'Loading branches…' : 'Select a branch'}</option>
                {branches.map(branch => <option key={branch.id} value={branch.id}>{branch.name} (ID: {branch.id}{branch.code ? ` / ${branch.code}` : ''})</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="item-sync-ref-code">
              <Form.Label>Item reference code</Form.Label>
              <Form.Control value={refCode} maxLength={100} required placeholder="e.g. 1007311" onChange={e => {setRefCode(e.target.value); clearResult();}} />
            </Form.Group>
          </Col>
          <Col md={3} className="d-flex gap-2">
            <Button type="submit" disabled={loading || branchesLoading || !branchId || !refCode.trim()}>{loading ? 'Checking…' : 'Check item'}</Button>
            <Button variant="outline-secondary" type="button" onClick={() => {setBranchId(''); setRefCode(''); setFrom(''); setTo(''); clearResult();}}>Clear</Button>
          </Col>
          <Col md={3}><Form.Group controlId="item-sync-from"><Form.Label>From date</Form.Label><Form.Control type="date" value={from} max={to || '9998-12-31'} onChange={e => {setFrom(e.target.value); clearResult();}} /></Form.Group></Col>
          <Col md={3}><Form.Group controlId="item-sync-to"><Form.Label>To date</Form.Label><Form.Control type="date" value={to} min={from || '1000-01-01'} max="9998-12-31" onChange={e => {setTo(e.target.value); clearResult();}} /></Form.Group></Col>
        </Row>
        <p className="small text-muted mt-2">Dates filter recorded changes and unmatched observations (last seen), including the full end date. Current stock/prices and the latest branch run always show the current saved state.</p>
      </Form>
      {loading && <div role="status" className="mt-3">Loading item sync records…</div>}
      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      {data && <div className="mt-3">
        <h6>{data.branch.name} (ID: {data.branch.id}) · Ref: {data.refCode}</h6>
        <p className="text-muted small">Branch POS / outlet ID: {value(data.branch.pos_id)}. Latest branch run: {value(data.lastBranchSync?.run_id)} · {value(data.lastBranchSync?.status)} · ended {time(data.lastBranchSync?.ended_at)}. This is branch-level status, not confirmation for this item.</p>
        <h6>Current database records</h6>
        {!data.items.length ? <Alert variant="warning">No catalog item matches this reference code.</Alert> : <Table responsive bordered size="sm">
          <thead><tr><th>Product / unit</th><th>Internal item ID</th><th>Inventory record</th><th>Branch stock</th><th>Branch MRP</th><th>Branch selling price</th><th>Inventory synced</th><th>Inventory updated</th><th>Catalog MRP / selling price</th><th>Catalog synced</th><th>Catalog updated</th><th>Record status</th></tr></thead>
          <tbody>{data.items.map(item => <tr key={`${item.product_item_id}-${item.inventory_id ?? 'missing'}`}>
            <td>{value(item.product_name)}<div className="text-muted">{value(item.unit_spec)} {value(item.unit_name)}</div></td>
            <td>{value(item.product_item_id)}<div className="text-muted small">GF ID: {value(item.gofrugal_item_id)}</div></td>
            <td>{item.inventory_id == null ? <Badge bg="warning" text="dark">Missing for branch</Badge> : <><Badge bg="success">Present</Badge> #{item.inventory_id}</>}</td>
            <td>{value(item.current_stock)}</td><td>{value(item.current_mrp)}</td><td>{value(item.current_sell_price)}</td>
            <td>{time(item.inventory_synced_at)}</td><td>{time(item.inventory_updated_at)}</td>
            <td>{value(item.catalog_mrp)} / {value(item.catalog_sell_price)}</td><td>{time(item.catalog_synced_at)}</td><td>{time(item.catalog_updated_at)}</td>
            <td>Product: {value(item.product_status)}<br />Item: {value(item.item_status)}<br />Inventory: {value(item.inventory_status)}</td>
          </tr>)}</tbody>
        </Table>}
        <p className="small text-muted">Branch prices come from the selected branch's inventory record. Catalog prices are shown separately. A missing sync timestamp does not mean the row is missing or that sync failed.</p>
        <h6>Saved unmatched GoFrugal observations</h6>
        {data.unmatched.length ? <><p className="small text-muted">These are saved observations; they may predate a catalog mapping added later.</p><Table responsive bordered size="sm">
          <thead><tr><th>Item</th><th>GF item ID</th><th>Raw stock</th><th>Raw MRP</th><th>Raw sale price</th><th>GF timestamp</th><th>Last seen</th><th>Run ID</th></tr></thead>
          <tbody>{data.unmatched.map((row, index) => <tr key={`${row.gofrugal_item_id}-${index}`}><td>{value(row.item_name)} ({value(row.uom)})</td><td>{value(row.gofrugal_item_id)}</td><td>{value(row.raw_stock)}</td><td>{value(row.raw_mrp)}</td><td>{value(row.raw_sale_price)}</td><td>{value(row.item_timestamp)}</td><td>{time(row.last_seen_at)}</td><td>{value(row.last_sync_run_id)}</td></tr>)}</tbody>
        </Table></> : <p>No saved unmatched observation for this item and branch.</p>}
        <h6>Recorded item changes ({data.changes.total})</h6>
        {data.changes.rows.length ? <Table responsive bordered size="sm">
          <thead><tr><th>Time</th><th>Run ID</th><th>Job / run status</th><th>Internal item ID</th><th>Item code</th><th>Change type</th><th>Old value</th><th>New value</th></tr></thead>
          <tbody>{data.changes.rows.map(row => <tr key={row.id}><td>{time(row.created_at)}</td><td>{value(row.run_id)}</td><td>{value(row.job_name)} / {value(row.run_status)}</td><td>{value(row.product_item_id)}</td><td>{value(row.item_code)}</td><td>{value(row.change_type)}</td><td>{value(row.old_value)}</td><td>{value(row.new_value)}</td></tr>)}</tbody>
        </Table> : <Alert variant="info">No retained item change history was found. This does not mean the item was never synced.</Alert>}
        {data.changes.total > PAGE_SIZE && <div className="d-flex gap-2 align-items-center">
          <Button size="sm" variant="outline-secondary" disabled={loading || applied.page === 0} onClick={() => changePage(applied.page - 1)}>Previous</Button>
          <span>Page {applied.page + 1} of {Math.ceil(data.changes.total / PAGE_SIZE)}</span>
          <Button size="sm" variant="outline-secondary" disabled={loading || (applied.page + 1) * PAGE_SIZE >= data.changes.total} onClick={() => changePage(applied.page + 1)}>Next</Button>
        </div>}
        <p className="small text-muted mt-2 mb-0">Read-only report from saved records. Audit history has limited retention and may contain monitor samples. No live GoFrugal request is made.</p>
      </div>}
    </Card.Body>
  </Card>;
}

